import { Joyride, STATUS, ACTIONS, EVENTS, Step, EventData, Controls } from "react-joyride";
import { useTour } from "@/contexts/useTour";

// ── Tour step definitions ─────────────────────────────────────────────────────
// Focused on the radar chart and Sage AI features.
// The TourContext navigates to the Radar 2D page before starting so that
// the radar-specific targets (toolbar, profiles, canvas) are in the DOM.
const TOUR_STEPS: Step[] = [
  // ── Step 1: Sidebar orientation ───────────────────────────────────────────
  {
    target: "[data-tour='sidebar']",
    title: "Welcome to Redefining Normal™",
    content:
      "This sidebar is your guide to the platform. It's organised into four sections — Understand, Explore, Verify, and Invest — so you can move through the research at your own pace.",
    placement: "right",
    skipBeacon: true,
  },

  // ── Step 2: Navigate to Radar 2D ─────────────────────────────────────────
  {
    target: "[data-tour='nav-radar']",
    title: "Radar 2D — The Core Visualisation",
    content:
      "The Radar 2D is the centrepiece of the platform. Click here at any time to open the interactive six-axis radar chart that maps relationship profiles across orientation, structure, ethics, population, growth, and visibility.",
    placement: "right",
    skipBeacon: true,
  },

  // ── Step 3: Radar toolbar ─────────────────────────────────────────────────
  {
    target: "[data-tour='radar-toolbar']",
    title: "Radar Toolbar",
    content:
      "The toolbar gives you quick controls: export the chart as a PNG, select a forecast year (current through +10), reset all sliders to defaults, and open the Variables panel to adjust individual axis values.",
    placement: "bottom",
    skipBeacon: true,
  },

  // ── Step 4: Profile toggles ───────────────────────────────────────────────
  {
    target: "[data-tour='radar-profiles']",
    title: "Profile Toggles",
    content:
      "Each coloured button represents a relationship profile — Traditional Norm, New Majority, CNM Only, Non-Faithful, Non-Hetero, and Two Dimensions. Click any profile to show or hide its polygon on the chart. Overlapping shapes reveal where groups converge or diverge.",
    placement: "bottom",
    skipBeacon: true,
  },

  // ── Step 5: The radar canvas ──────────────────────────────────────────────
  {
    target: "[data-tour='radar-canvas']",
    title: "The Radar Chart",
    content:
      "This is the spiderweb. Each spoke represents one of the six axes, and the further a vertex extends from the centre, the higher that profile scores on that dimension. The shape of the polygon — not any single number — is where the meaning lives.",
    placement: "left",
    skipBeacon: true,
  },

  // ── Step 6: Variables panel ───────────────────────────────────────────────
  {
    target: "[data-tour='radar-toolbar']",
    title: "Variables Panel",
    content:
      "Click the Variables button (sliders icon) in the toolbar to open the Variables panel. Use the sliders to adjust each axis value for any profile and watch the polygon reshape in real time. Hit Reset to restore the research-backed defaults.",
    placement: "bottom",
    skipBeacon: true,
  },

  // ── Step 7: Reading the Radar explainer ──────────────────────────────────
  {
    target: "[data-tour='nav-explainer']",
    title: "Reading the Radar",
    content:
      "New to radar charts? This guided walkthrough explains each axis, shows you how to spot crossings and trajectory gaps, and walks through all six profiles step by step. It's the fastest way to build intuition for the data.",
    placement: "right",
    skipBeacon: true,
  },

  // ── Step 8: Sage AI assistant ─────────────────────────────────────────────
  {
    target: "[data-tour='sage-trigger']",
    title: "Meet Sage",
    content:
      "Sage is your AI research assistant. Ask any question about the radar data, the thesis, a specific profile, or the research sources. Sage draws from the pitch deck, Instagram posts, and all peer-reviewed citations — and answers in plain language.",
    placement: "left",
    skipBeacon: true,
  },

  // ── Step 9: Restart tour ──────────────────────────────────────────────────
  {
    target: "[data-tour='tour-restart']",
    title: "Restart This Tour Anytime",
    content:
      "You can replay this tour whenever you like by clicking Guided Tour in the sidebar. Enjoy exploring Redefining Normal™!",
    placement: "right",
    skipBeacon: true,
  },
];

// ── Joyride colour palette (matches the app's sage green primary) ─────────────
const JOYRIDE_STYLES = {
  tooltip: {
    borderRadius: 14,
    padding: "22px 26px",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    maxWidth: 360,
  },
  tooltipTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 8,
    color: "#111827",
  },
  tooltipContent: {
    fontSize: 13.5,
    lineHeight: 1.65,
    color: "#374151",
  },
  buttonPrimary: {
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 600,
    backgroundColor: "#47825F",
  },
  buttonBack: {
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 500,
    color: "#6b7280",
    marginRight: 6,
  },
  buttonSkip: {
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    color: "#9ca3af",
  },
};

// ── Component ─────────────────────────────────────────────────────────────────
export function AppTour() {
  const { isRunning, stopTour } = useTour();

  const handleEvent = (data: EventData, _controls: Controls) => {
    const { status, action, type } = data;

    // Tour finished or skipped
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(status)) {
      stopTour();
      return;
    }

    // User closed via the X button
    if (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER) {
      stopTour();
    }
  };

  return (
    <Joyride
      steps={TOUR_STEPS}
      run={isRunning}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      options={{
        showProgress: true,
        buttons: ["back", "close", "primary", "skip"],
        primaryColor: "#47825F",
        overlayColor: "rgba(0,0,0,0.45)",
        skipBeacon: true,
      }}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish Tour",
        next: "Next",
        nextWithProgress: "Next ({current} of {total})",
        skip: "Skip Tour",
      }}
      styles={JOYRIDE_STYLES}
    />
  );
}
